
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useInshurLanding, CardLink } from "../landingMode";
import { ArrowRight } from "lucide-react";
/* Shared "is this card's animation playing" flag. A card plays its preview animation only when
   it is active: on hover for pointer/desktop devices, or when scrolled into view on touch/mobile
   devices. Otherwise the flag is false and each preview rests on the first frame of its animation.
   Payments opts out (gated={false}) so it keeps looping exactly as before. */
const FeatureCardPlayingContext = createContext(false);
export const useFeatureCardPlaying = () => useContext(FeatureCardPlayingContext);
export function FeatureCard({ href, title, copy, preview, gated = true, hideLearnMore, }) {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    const [hover, setHover] = useState(false);
    const [hoverCapable, setHoverCapable] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined")
            return;
        setHoverCapable(window.matchMedia("(hover: hover) and (pointer: fine)").matches);
    }, []);
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries)
                setInView(entry.isIntersecting);
        }, { threshold: 0.35 });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    const playing = !gated ? true : hoverCapable ? hover : inView;
    const inshur = useInshurLanding(); // /inshur: cards are not links
    const showLearnMore = !(hideLearnMore ?? inshur); // /tnt-group passes hideLearnMore explicitly
    return (<FeatureCardPlayingContext.Provider value={playing}>
      <CardLink ref={ref} href={href} data-fc-playing={playing ? "true" : "false"} onPointerEnter={() => setHover(true)} onPointerLeave={() => setHover(false)} className="group/card relative flex min-h-[35rem] flex-col rounded-xl bg-[#8A5CFF]/[0.05] p-6 backdrop-blur-md transition-colors hover:bg-[#8A5CFF]/[0.09] sm:rounded-2xl sm:p-7">
        {/* Name header. */}
        <div className="flex items-center">
          <h4 className="font-sans text-xl font-medium leading-tight tracking-tight text-white">{title}</h4>
        </div>

        {/* Animated preview. */}
        <div className="mt-5 flex flex-1 items-center justify-center">{preview}</div>

        {/* Subcopy and Learn more. */}
        <p className="mt-5 font-sans text-base leading-relaxed text-white/60 text-pretty">{copy}</p>
        {showLearnMore && (
        <span className="mt-5 inline-flex items-center gap-2 align-middle text-base font-normal text-white">
          <span className="underline decoration-[#8A5CFF]/50 underline-offset-4 transition-colors group-hover/card:decoration-[#8A5CFF]">
            Learn more
          </span>
          <ArrowRight aria-hidden="true" className="learn-more-arrow h-5 w-5 shrink-0"/>
        </span>
        )}
      </CardLink>
    </FeatureCardPlayingContext.Provider>);
}
